import { GA_MEASUREMENT_ID } from '@/shared/consts'

type GtagCommand = 'config' | 'event' | 'set' | 'js' | 'consent'

interface GtagEventParams {
  [key: string]: string | number | boolean | undefined
}

interface EcommerceItem {
  item_id: string
  item_name: string
  price?: number
  quantity?: number
  item_category?: string
}

interface EcommerceParams {
  currency?: string
  value?: number
  items?: EcommerceItem[]
}

declare global {
  interface Window {
    gtag: (
      command: GtagCommand,
      target: string | Date,
      params?:
      | GtagEventParams
      | EcommerceParams
      | { user_id?: string }
      | { user_data?: { sha256_email_address: string } }
    ) => void
    dataLayer: unknown[]
  }
}

const isGtagAvailable = (): boolean => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function'
}

// SHA-256 hash function for enhanced conversions
async function sha256Hash(message: string): Promise<string> {
  const msgBuffer = new TextEncoder().encode(message)
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer)
  const hashArray = Array.from(new Uint8Array(hashBuffer))

  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
}

export function trackEvent(eventName: string, params?: GtagEventParams): void {
  if (!isGtagAvailable()) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Event:', eventName, params)
    }

    return
  }

  window.gtag('event', eventName, params)
}

export function trackEcommerce(
  eventName:
  | 'view_item'
  | 'view_item_list'
  | 'select_item'
  | 'add_to_cart'
  | 'remove_from_cart'
  | 'begin_checkout',
  items: EcommerceItem[],
  value?: number,
  currency: string = 'AUD',
): void {
  if (!isGtagAvailable()) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Ecommerce:', eventName, { items, value, currency })
    }

    return
  }

  window.gtag('event', eventName, {
    currency,
    value,
    items,
  })
}


export function setUserId(userId: string): void {
  if (!isGtagAvailable()) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Set user ID:', userId)
    }

    return
  }

  window.gtag('config', GA_MEASUREMENT_ID, {
    user_id: userId,
  })
}

export async function setUserData(email: string): Promise<void> {
  if (!isGtagAvailable()) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Set user data:', email)
    }

    return
  }

  const normalizedEmail = email.toLowerCase().trim()
  const hashedEmail = await sha256Hash(normalizedEmail)

  window.gtag('set', 'user_data', {
    sha256_email_address: hashedEmail,
  })
}

export function clearUserData(): void {
  if (!isGtagAvailable()) return

  window.gtag('set', 'user_data', {
    sha256_email_address: '',
  })
}

export type { EcommerceItem, GtagEventParams }
